import { useState, useEffect } from "react";
import { trpc } from "../../../trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert } from "@/components/ui/alert";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import { SearchableSelect } from "@/components/ui/searchable-select";

type ProjectData = {
  id: string;
  merchantId: string;
  code: string;
  name: string;
  calId?: string;
  calLink?: string;
  appointmentsHours?: {
    weekDayStart?: string;
    weekDayEnd?: string;
    visitOnSaturdayAllowed?: boolean;
    visitOnSundayAllowed?: boolean;
    saturdayDayStart?: string;
    saturdayDayEnd?: string;
    sundayDayStart?: string;
    sundayDayEnd?: string;
    minimumAdvanceHours?: number;
  };
};

type StepProjectProps = {
  merchantId: string;
  onSubmit: (data: ProjectData, createNew: boolean) => Promise<void>;
  initialData: ProjectData | null;
  isLoading: boolean;
};

export const StepProject = ({ merchantId, onSubmit, initialData, isLoading }: StepProjectProps) => {
  const [mode, setMode] = useState<"select" | "create">("select");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [formData, setFormData] = useState<ProjectData>({
    id: "",
    merchantId,
    code: "",
    name: "",
    calId: "",
    calLink: "",
    appointmentsHours: {
      weekDayStart: "10:00 AM",
      weekDayEnd: "6:30 PM",
      visitOnSaturdayAllowed: true,
      visitOnSundayAllowed: true,
      saturdayDayStart: "10:00 AM",
      saturdayDayEnd: "6:30 PM",
      sundayDayStart: "12:00 PM",
      sundayDayEnd: "5:00 PM",
      minimumAdvanceHours: 0,
    },
  });

  const projectsQuery = trpc.agent.projectsWithoutAgents.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );
  const { data: projects, isLoading: projectsLoading, error: projectsError } = projectsQuery;

  useEffect(() => {
    if (initialData) {
      if (projects?.some((p) => p.id === initialData.id)) {
        setMode("select");
        setSelectedProjectId(initialData.id);
      } else {
        setMode("create");
        setFormData(initialData);
      }
    }
  }, [initialData, projects]);

  const handleModeChange = (value: string) => {
    setMode(value as "select" | "create");
    if (value === "select") {
      setSelectedProjectId("");
    } else {
      setFormData({
        id: "",
        merchantId,
        code: "",
        name: "",
        calId: "",
        calLink: "",
        appointmentsHours: {
          weekDayStart: "10:00 AM",
          weekDayEnd: "6:30 PM",
          visitOnSaturdayAllowed: true,
          visitOnSundayAllowed: true,
          saturdayDayStart: "10:00 AM",
          saturdayDayEnd: "6:30 PM",
          sundayDayStart: "12:00 PM",
          sundayDayEnd: "5:00 PM",
          minimumAdvanceHours: 0,
        },
      });
    }
  };

  const handleInputChange = (field: keyof ProjectData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAppointmentHoursChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      appointmentsHours: {
        ...prev.appointmentsHours,
        [field]: value,
      },
    }));
  };

  const handleSubmit = async () => {
    if (mode === "select") {
      const selectedProject = projects?.find((p) => p.id === selectedProjectId);
      if (selectedProject) {
        await onSubmit(selectedProject, false);
      }
    } else {
      await onSubmit(formData, true);
    }
  };

  const isFormValid = () => {
    if (mode === "select") {
      return selectedProjectId !== "";
    } else {
      return formData.code && formData.name;
    }
  };

  if (!merchantId) {
    return (
      <Alert status="warning">
        Por favor selecciona un merchant primero
      </Alert>
    );
  }

  if (projectsLoading) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <Spinner className="h-8 w-8" />
        <p>Cargando proyectos disponibles...</p>
      </div>
    );
  }

  if (projectsError) {
    return (
      <Alert status="error">
        Error loading projects: {projectsError.message}
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <p className="text-lg font-semibold mb-4">Paso 2: Seleccionar o crear proyecto</p>

        <RadioGroup value={mode} onValueChange={handleModeChange}>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <RadioGroupItem value="select" id="selectProject" />
              <Label htmlFor="selectProject" className="font-normal cursor-pointer">
                Seleccionar proyecto existente (sin agente)
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="create" id="createProject" />
              <Label htmlFor="createProject" className="font-normal cursor-pointer">
                Crear nuevo proyecto
              </Label>
            </div>
          </div>
        </RadioGroup>
      </div>

      <hr />

      {mode === "select" ? (
        <div className="flex flex-col gap-2">
          <Label htmlFor="projectSelect">Proyecto *</Label>
          {projects && projects.length > 0 ? (
            <SearchableSelect
              id="projectSelect"
              value={selectedProjectId}
              onValueChange={setSelectedProjectId}
              placeholder="Selecciona un proyecto..."
              searchPlaceholder="Buscar proyecto..."
              items={projects.map((project) => ({
                value: project.id,
                label: `${project.name} (${project.code})`,
              }))}
            />
          ) : (
            <Alert status="info">
              No hay proyectos disponibles sin agente. Crea un nuevo proyecto.
            </Alert>
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="projectCode">Código *</Label>
            <Input
              id="projectCode"
              value={formData.code}
              onChange={(e) => handleInputChange("code", e.target.value)}
              placeholder="Ingresa el código del proyecto"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="projectName">Nombre *</Label>
            <Input
              id="projectName"
              value={formData.name}
              onChange={(e) => handleInputChange("name", e.target.value)}
              placeholder="Ingresa el nombre del proyecto"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="calId">Cal ID</Label>
            <Input
              id="calId"
              value={formData.calId}
              onChange={(e) => handleInputChange("calId", e.target.value)}
              placeholder="Ingresa el Cal ID"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="calLink">Cal Link</Label>
            <Input
              id="calLink"
              value={formData.calLink}
              onChange={(e) => handleInputChange("calLink", e.target.value)}
              placeholder="Ingresa el Cal Link"
            />
          </div>

          <div>
            <p className="text-md font-semibold mb-4">Horarios de citas</p>

            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <Label htmlFor="weekDayStart">Inicio días laborales</Label>
                  <Input
                    id="weekDayStart"
                    value={formData.appointmentsHours?.weekDayStart}
                    onChange={(e) => handleAppointmentHoursChange("weekDayStart", e.target.value)}
                    placeholder="10:00 AM"
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <Label htmlFor="weekDayEnd">Fin días laborales</Label>
                  <Input
                    id="weekDayEnd"
                    value={formData.appointmentsHours?.weekDayEnd}
                    onChange={(e) => handleAppointmentHoursChange("weekDayEnd", e.target.value)}
                    placeholder="6:30 PM"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <Switch
                    id="saturdayAllowed"
                    checked={formData.appointmentsHours?.visitOnSaturdayAllowed}
                    onCheckedChange={(checked) =>
                      handleAppointmentHoursChange("visitOnSaturdayAllowed", checked)
                    }
                  />
                  <Label htmlFor="saturdayAllowed" className="font-normal cursor-pointer">
                    Visitas permitidas sábados
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    id="sundayAllowed"
                    checked={formData.appointmentsHours?.visitOnSundayAllowed}
                    onCheckedChange={(checked) =>
                      handleAppointmentHoursChange("visitOnSundayAllowed", checked)
                    }
                  />
                  <Label htmlFor="sundayAllowed" className="font-normal cursor-pointer">
                    Visitas permitidas domingos
                  </Label>
                </div>
              </div>

              {formData.appointmentsHours?.visitOnSaturdayAllowed && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="saturdayStart">Inicio sábados</Label>
                    <Input
                      id="saturdayStart"
                      value={formData.appointmentsHours?.saturdayDayStart}
                      onChange={(e) =>
                        handleAppointmentHoursChange("saturdayDayStart", e.target.value)
                      }
                      placeholder="10:00 AM"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="saturdayEnd">Fin sábados</Label>
                    <Input
                      id="saturdayEnd"
                      value={formData.appointmentsHours?.saturdayDayEnd}
                      onChange={(e) =>
                        handleAppointmentHoursChange("saturdayDayEnd", e.target.value)
                      }
                      placeholder="6:30 PM"
                    />
                  </div>
                </div>
              )}

              {formData.appointmentsHours?.visitOnSundayAllowed && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex flex-col gap-2">
                    <Label htmlFor="sundayStart">Inicio domingos</Label>
                    <Input
                      id="sundayStart"
                      value={formData.appointmentsHours?.sundayDayStart}
                      onChange={(e) =>
                        handleAppointmentHoursChange("sundayDayStart", e.target.value)
                      }
                      placeholder="12:00 PM"
                    />
                  </div>

                  <div className="flex flex-col gap-2">
                    <Label htmlFor="sundayEnd">Fin domingos</Label>
                    <Input
                      id="sundayEnd"
                      value={formData.appointmentsHours?.sundayDayEnd}
                      onChange={(e) =>
                        handleAppointmentHoursChange("sundayDayEnd", e.target.value)
                      }
                      placeholder="5:00 PM"
                    />
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-2">
                <Label htmlFor="minAdvanceHours">Horas mínimas de anticipación</Label>
                <Input
                  id="minAdvanceHours"
                  type="number"
                  min={0}
                  value={formData.appointmentsHours?.minimumAdvanceHours}
                  onChange={(e) =>
                    handleAppointmentHoursChange("minimumAdvanceHours", parseInt(e.target.value) || 0)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <Button onClick={handleSubmit} disabled={!isFormValid() || isLoading}>
          {isLoading ? "Procesando..." : "Continuar"}
        </Button>
      </div>
    </div>
  );
};
